// Data layer for the Income Runs admin page. Reuses the env-aware
// fetchWithFallback helper from OrgUsageAnalytics so prod/dev routing, the
// double `/admin` prefix, and the userId query param all behave identically.
import { fetchWithFallback } from "../OrgUsageAnalytics/api";

const BASE = "/income-calculation-jobs";
const GOLDEN_BASE = "/income-golden-loans";

/**
 * List income calculation runs. When organizationId is provided the list is
 * org-scoped; otherwise it returns the most recent runs across orgs.
 */
export const fetchIncomeRuns = ({ organizationId, limit } = {}, env) =>
  fetchWithFallback(BASE, { organizationId, limit }, { env, timeoutMs: 30000 });

/** Fetch one run plus its compact GCS artifacts for the tree view. */
export const fetchIncomeRunDetail = (jobId, env) =>
  fetchWithFallback(`${BASE}/${jobId}`, {}, { env, timeoutMs: 60000 });

/** Queue an existing dev loan or an asynchronous prod-to-dev clone. */
export const enqueueIncomeRun = (payload, env) =>
  fetchWithFallback(
    `${BASE}/enqueue`,
    {},
    { env, method: "POST", body: payload, timeoutMs: 30000 },
  );

/**
 * Ask a question about a run. The conversation is stateless server-side, so
 * we send the full message history each turn.
 */
export const askIncomeRun = (jobId, messages, env) =>
  fetchWithFallback(
    `${BASE}/${jobId}/chat`,
    {},
    { env, method: "POST", body: { messages }, timeoutMs: 120000 },
  );

/** List loans in the golden dataset. */
export const fetchGoldenLoans = ({ limit } = {}, env) =>
  fetchWithFallback(
    GOLDEN_BASE,
    { limit },
    { env, timeoutMs: 30000 },
  );

/** Add a Dev loan to the golden dataset. */
export const addGoldenLoan = (payload, env) =>
  fetchWithFallback(
    GOLDEN_BASE,
    {},
    { env, method: "POST", body: payload, timeoutMs: 30000 },
  );

/** Remove a loan from the golden dataset. */
export const removeGoldenLoan = (goldenLoanId, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}`,
    {},
    { env, method: "DELETE", timeoutMs: 30000 },
  );

/** Enqueue one or all golden loans through the income pipeline. */
export const runGoldenSuite = (payload, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/run`,
    {},
    { env, method: "POST", body: payload || {}, timeoutMs: 60000 },
  );

/** Latest aggregate golden-suite runs. */
export const fetchGoldenSuiteRuns = ({ limit = 10 } = {}, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/suite-runs`,
    { limit },
    { env, timeoutMs: 30000 },
  );

/** Aggregate run metadata plus one evaluation row per golden loan. */
export const fetchGoldenSuiteRun = (suiteRunId, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/suite-runs/${encodeURIComponent(suiteRunId)}`,
    {},
    { env, timeoutMs: 60000 },
  );

/** Save an optional normalized human baseline for one golden loan. */
export const saveGoldenHumanLabel = (goldenLoanId, humanLabel, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}/human-label`,
    {},
    {
      env,
      method: "POST",
      body: { humanLabel },
      timeoutMs: 30000,
    },
  );

/** Re-run the judge for a loan against an existing suite run. */
export const analyzeGoldenLoan = (goldenLoanId, suiteRunId, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}/analyze`,
    {},
    {
      env,
      method: "POST",
      body: { suiteRunId },
      timeoutMs: 120000,
    },
  );

/** Clone a Prod loan into Dev and add it to the golden dataset. */
export const cloneGoldenLoan = (payload, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/clone`,
    {},
    { env, method: "POST", body: payload, timeoutMs: 120000 },
  );

/** Generate a concise explanation for one Addy/Harness difference. */
export const explainGoldenDiff = (
  goldenLoanId,
  { suiteRunId, leftSource, rightSource, path },
  env,
) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}/explain-diff`,
    {},
    {
      env,
      method: "POST",
      body: { suiteRunId, leftSource, rightSource, path },
      timeoutMs: 120000,
    },
  );

/** Version history headers for one golden loan. */
export const fetchGoldenVersions = (goldenLoanId, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}/versions`,
    {},
    { env, timeoutMs: 30000 },
  );

/** Loan → BR → EMP snapshot for one version run. */
export const fetchGoldenVersionSnapshot = (goldenLoanId, jobId, env) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}/versions/${encodeURIComponent(jobId)}`,
    {},
    { env, timeoutMs: 60000 },
  );

/** Compare two version runs for one golden loan. */
export const compareGoldenVersions = (
  goldenLoanId,
  { leftJobId, rightJobId },
  env,
) =>
  fetchWithFallback(
    `${GOLDEN_BASE}/${encodeURIComponent(goldenLoanId)}/compare`,
    { leftJobId, rightJobId },
    { env, timeoutMs: 60000 },
  );
